import {
  CartForm,
  CollectionGrid,
  DividerPhoto,
  Grid,
  GridSize,
  Hero,
  ProductHero,
  ReviewWidget,
  Video,
  Marquee,
  ShopSort,
  ShopFilter,
  BlogPostGrid,
  BlogPostHeader,
  BlogCategoryPostGrid,
  BlogAuthorPostGrid,
  SanityColorCode,
} from '@data/sanity/schema'
import { Keyed } from '@lib/helpers'
import { SanityBlocks } from './blocks'
import {
  SanityBlogAuthor,
  SanityBlogCategory,
  SanityBlogPostOptions,
  SanityBlogPostWithoutBody,
} from './blog'
import { SanityContentFragment } from './content'
import { SanityImageFragment } from './image'
import { SanityHasSlug } from './page'
import { SanityProductFragment, SanityProductId } from './product'
import { HasCollectionStrings, HasReviewsStrings, SanityCart } from './site'
import { SanityMuxVideo, SanityVideo, SanityVimeoVideoMedia } from './video'

export enum SanityModuleType {
  GRID = 'grid',
  HERO = 'hero',
  DIVIDER_PHOTO = 'dividerPhoto',
  PRODUCT_HERO = 'productHero',
  COLLECTION_GRID = 'collectionGrid',
  REVIEW_WIDGET = 'reviewWidget',
  CART_FORM = 'cartForm',
  VIDEO = 'video',
  MARQUEE = 'marquee',
  BLOG_POST_GRID = 'blogPostGrid',
  BLOG_POST_HEADER = 'blogPostHeader',
  BLOG_CATEGORY_POST_GRID = 'blogCategoryPostGrid',
  BLOG_AUTHOR_POST_GRID = 'blogAuthorPostGrid',
}

// Module component types
export type SanityGridColumnSize = Pick<
  GridSize,
  'breakpoint' | 'width' | 'justify' | 'align' | 'start'
>

export type SanityGridColumn = Keyed<{
  sizes: Array<SanityGridColumnSize>
  blocks: Array<SanityBlocks>
  style: string
}>

export type SanityGrid = Pick<
  Grid,
  'size' | 'style' | 'noColumnGaps' | 'noRowGaps' | 'reverseSequence'
> & {
  columns: Array<SanityGridColumn>
}

export interface SanityHeroPhotos {
  mobilePhoto?: SanityImageFragment
  desktopPhoto?: SanityImageFragment
}

export type SanityHeroVimeoVideo = Pick<SanityVimeoVideoMedia, 'link' | 'name'>

export enum SanityHeroBackgroundType {
  PHOTO = 'photo',
  VIMEO_VIDEO = 'video',
  MUX_VIDEO = 'mux-video',
}

export enum SanityHeroContentPosition {
  CENTER = 'center',
  BOTTOM_LEFT = 'bottom-left',
}

export interface SanityHero {
  bgType: SanityHeroBackgroundType
  contentPosition: SanityHeroContentPosition
  content?: Array<SanityContentFragment>
  photos?: SanityHeroPhotos
  vimeoVideo?: SanityHeroVimeoVideo
  muxVideo?: SanityMuxVideo
}

export interface SanityDividerPhoto {
  photo: SanityImageFragment
}

export type SanityProductHero = Pick<ProductHero, 'active'> & {
  product?: SanityProductFragment
}

export enum SanityFilterType {
  SIMPLE = ' ',
  SWATCH = 'swatch',
}

export enum SanityFilterGroupDisplay {
  DEFAULT = ' ',
  GRID = 'grid',
}

export type SanityFilterGroupOption = SanityHasSlug & {
  type: SanityFilterType
  title: string
  color?: SanityColorCode
}

export type SanityFilterGroup = Keyed<
  SanityHasSlug & {
    display: SanityFilterGroupDisplay
    title: string
    options: Array<SanityFilterGroupOption>
  }
>

export type SanityFilter = Pick<ShopFilter, 'isActive'> & {
  groups?: Array<SanityFilterGroup>
}

export enum SanitySortOptionType {
  FEATURED = 'featured',
  ALPHA_ASCENDING = 'alphaAsc',
  ALPHA_DESCENDING = 'alphaDesc',
  DATE_ASCENDING = 'dateAsc',
  DATE_DESCENDING = 'dateDesc',
  PRICE_ASCENDING = 'priceAsc',
  PRICE_DESCENDING = 'priceDesc',
}

export type SanitySortOption = {
  type: SanitySortOptionType
  title: string
}

export type SanitySort = Pick<ShopSort, 'isActive'> & {
  options?: Array<SanitySortOption>
}

export type SanityCollectionGrid = Pick<CollectionGrid, 'active'> &
  HasCollectionStrings & {
    collection?: {
      title?: string
      featuredProductIds?: Array<SanityProductId>
      products?: Array<SanityProductFragment>
    }
    shop?: {
      featuredProductIds?: Array<SanityProductId>
      products?: Array<SanityProductFragment>
    }
    cartSettings?: {
      paginationLimit: number
      filter?: SanityFilter
      sort?: SanitySort
    }
  }

export enum SanityReviewWidgetType {
  MAIN = 'main',
  PRODUCT = 'product',
}

export type SanityReviewWidget = HasReviewsStrings & {
  type: SanityReviewWidgetType
}

export type SanityCartForm = Pick<CartForm, 'active'> & {
  cartSettings?: SanityCart
}

export type SanityMarqueeSimpleItem = {
  _type: 'simple'
  text: string
}

export type SanityMarqueePhotoItem = {
  _type: 'photo'
  photo: SanityImageFragment
}

export type SanityMarqueeProductItem = {
  _type: 'product'
  _id: string
  product: SanityProductFragment
}

export type SanityMarqueeItem = Keyed<
  SanityMarqueeSimpleItem | SanityMarqueePhotoItem | SanityMarqueeProductItem
>

export type SanityMarquee = Pick<Marquee, 'speed' | 'reverse' | 'pausable'> & {
  items: Array<SanityMarqueeItem>
}

export interface SanityBlogPostGrid {
  posts: Array<SanityBlogPostWithoutBody>
  author?: SanityBlogAuthor
  category?: SanityBlogCategory
  options?: SanityBlogPostOptions
}

export interface SanityBlogPostHeader {
  post?: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
}

export interface SanityBlogPostBody {
  content: SanityContentFragment
}

export interface SanityBlogPostReadMore {
  posts: Array<SanityBlogPostWithoutBody>
}

export type SanityBlogCategoryPostGrid = Pick<SanityBlogPostGrid, 'options'> & {
  posts: Array<SanityBlogPostWithoutBody>
}

export type SanityBlogAuthorPostGrid = Pick<SanityBlogPostGrid, 'options'> & {
  posts: Array<SanityBlogPostWithoutBody>
}

// Module data types
export type SanityGridModule = Keyed<Pick<Grid, '_type'> & SanityGrid>

export type SanityHeroModule = Keyed<Pick<Hero, '_type'> & SanityHero>

export type SanityDividerPhotoModule = Keyed<
  Pick<DividerPhoto, '_type'> & SanityDividerPhoto
>

export type SanityProductHeroModule = Keyed<Pick<ProductHero, '_type'>> &
  SanityProductHero

export type SanityCollectionGridModule = Keyed<
  Pick<CollectionGrid, '_type'> & SanityCollectionGrid
>

export type SanityReviewWidgetModule = Keyed<
  Pick<ReviewWidget, '_type'> & SanityReviewWidget
>

export type SanityCartFormModule = Keyed<
  Pick<CartForm, '_type'> & SanityCartForm
>

export type SanityVideoModule = Keyed<Pick<Video, '_type'> & SanityVideo>

export type SanityMarqueeModule = Keyed<Pick<Marquee, '_type'> & SanityMarquee>

export type SanityBlogPostGridModule = Keyed<
  Pick<BlogPostGrid, '_type'> & SanityBlogPostGrid
>

export type SanityBlogPostHeaderModule = Keyed<
  Pick<BlogPostHeader, '_type'> & SanityBlogPostHeader
>

export type SanityBlogCategoryPostGridModule = Keyed<
  Pick<BlogCategoryPostGrid, '_type'> & SanityBlogCategoryPostGrid
>

export type SanityBlogAuthorPostGridModule = Keyed<
  Pick<BlogAuthorPostGrid, '_type'> & SanityBlogAuthorPostGrid
>

export type SanityModule =
  | SanityGridModule
  | SanityHeroModule
  | SanityDividerPhotoModule
  | SanityProductHeroModule
  | SanityCollectionGridModule
  | SanityReviewWidgetModule
  | SanityCartFormModule
  | SanityVideoModule
  | SanityMarqueeModule
  | SanityBlogPostGridModule
  | SanityBlogPostHeaderModule
  | SanityBlogCategoryPostGridModule
  | SanityBlogAuthorPostGridModule
